import type { UserOverview } from '@racemap/sdk/schema/user';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { useCallback, useMemo } from 'react';
import { useUserSearch } from 'src/lib/customHooks';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useDebounceValue } from 'src/lib/customHooks/useDebounce';
import { useEventsPaginated } from 'src/lib/customHooks/useEventsPaginated';
import { useFuzzySearch } from 'src/lib/customHooks/useFuzzySearch';
import { useIsAdmin } from 'src/lib/customHooks/useIsAdmin';
import { useIsChildAccount } from 'src/lib/customHooks/useIsChildAccount';
import type { UserAction } from '../useUserActions';
import { PageSearchFields, type SearchablePage, SearchablePages } from './searchablePages';
import { useAccountActions } from './useAccountActions';
import { useRecentSearches } from './useRecentSearches';

export const MinQueryLength = 2;
const DebounceTimeInMillis = 250;
/** Every group has to stay above the fold when All blends them into one panel. */
const EventsShownUnderAll = 3;
const QuickActionCount = 3;
const UserSearchLimit = 5;
// Module-level: useFuzzySearch keys its Fuse memo on this reference.
const ActionSearchFields: Array<keyof UserAction> = ['title', 'keywords'];

export interface EventSearchItem {
  kind: 'event';
  key: string;
  eventId: string;
  path: string;
  event: RacemapEvent;
}

export interface PageSearchItem {
  kind: 'page';
  key: string;
  path: string;
  page: SearchablePage;
}

/** Admin-only: the back office has no user pages for anybody else. */
export interface UserSearchItem {
  kind: 'user';
  key: string;
  path: string;
  user: UserOverview;
}

/** What the inspected account's UserLink menu offers. Only some of them navigate. */
export interface ActionSearchItem {
  kind: 'action';
  key: string;
  action: UserAction;
}

/** Picking one refills the field rather than navigating. */
export interface RecentSearchItem {
  kind: 'recent';
  key: string;
  query: string;
}

export type SearchItem =
  | EventSearchItem
  | PageSearchItem
  | UserSearchItem
  | ActionSearchItem
  | RecentSearchItem;

export interface SearchGroup {
  key: string;
  title: string;
  items: Array<SearchItem>;
}

export enum SearchCategory {
  All = 'all',
  Events = 'events',
  Pages = 'pages',
  Users = 'users',
}

export interface SearchTab {
  key: SearchCategory;
  title: string;
  count: number;
  /** The API gives no total, so the count is what is loaded and grows as you scroll. */
  hasMore: boolean;
}

interface SearchResults {
  groups: Array<SearchGroup>;
  /** The index space the keyboard cursor moves in, flattened out of `groups`. */
  items: Array<SearchItem>;
  /** Only the categories that matched, plus All. Empty below two. */
  tabs: Array<SearchTab>;
  /** Falls back to All when the chosen category stops matching. */
  activeCategory: SearchCategory;
  hasError: boolean;
  retry: () => void;
  /** The query the results answer — what the rows highlight against. */
  debouncedQuery: string;
  /** True once the query is long enough to search for. */
  isActive: boolean;
  isLoading: boolean;
  hasNextPage: boolean;
  loadNextPage: () => void;
}

export const useSearchResults = (query: string, category: SearchCategory): SearchResults => {
  const trimmedQuery = query.trim();
  const [debouncedQuery] = useDebounceValue(trimmedQuery, DebounceTimeInMillis);
  // Both queries: gating on the debounced one alone keeps the panel up for one debounce after
  // the field is cleared.
  const isActive = trimmedQuery.length >= MinQueryLength && debouncedQuery.length >= MinQueryLength;
  const currentUser = useCurrentUser();
  const isAdmin = useIsAdmin();
  const isChildAccount = useIsChildAccount();
  // Keyed by user: a shared browser must not show one account's queries to the next.
  const { recentSearches } = useRecentSearches(currentUser?.id ?? null);

  // Mirrors the Tracking Maps list, so search never offers an event the user cannot open.
  const {
    data,
    isLoading,
    error,
    retry: retryEvents,
    hasNextPage,
    loadNextPage,
  } = useEventsPaginated({
    searchValue: debouncedQuery,
    show: ['hidden', 'parentsonly'],
    filter: 'can-edit',
    fetchDisabled: !isActive,
    // Rows are actionable: keeping the previous ones would let ⏎ open an event from the query
    // before this one.
    keepPreviousData: false,
    pageSize: 10,
  });

  // Failure suppresses the rows for the same reason.
  const hasError = error != null;
  const eventItems =
    isActive && !hasError
      ? // Paged Meilisearch results can repeat an event across page boundaries.
        data
          .filter((event, index) => index === data.findIndex((other) => other.id === event.id))
          .map(
            (event): EventSearchItem => ({
              kind: 'event',
              key: `event-${event.id}`,
              eventId: event.id,
              path: `/admin/events/${event.id}`,
              event,
            }),
          )
      : [];

  const visiblePages = useMemo(
    () =>
      SearchablePages.filter(
        (page) => (isAdmin || !page.adminOnly) && !(isChildAccount && page.childHidden),
      ),
    [isAdmin, isChildAccount],
  );
  // 0: debouncedQuery is already debounced, and useFuzzySearch would debounce again on top of it.
  const matchedPages = useFuzzySearch(visiblePages, debouncedQuery, PageSearchFields, 0);
  const toPageItem = (page: SearchablePage): PageSearchItem => ({
    kind: 'page',
    key: `page-${page.path}`,
    path: page.path,
    page,
  });
  // useFuzzySearch answers an empty query with the whole array, so this has to stay gated.
  const pageItems: Array<PageSearchItem> = isActive ? matchedPages.map(toPageItem) : [];
  // The list is ordered so its head is the quick actions — no second flag needed.
  const quickActions: Array<PageSearchItem> = isActive
    ? []
    : visiblePages.slice(0, QuickActionCount).map(toPageItem);

  const {
    users,
    isLoading: isLoadingUsers,
    error: usersError,
    retry: retryUsers,
  } = useUserSearch(
    // null disables the key, so a non-admin never asks an endpoint that would answer 403.
    isAdmin && isActive ? debouncedQuery : null,
    UserSearchLimit,
    // Rows are actionable: the same reason the event hook opts out of keeping previous data.
    { keepPreviousData: false, refreshInterval: 0 },
  );
  const userItems: Array<UserSearchItem> = (users ?? []).map((user) => ({
    kind: 'user',
    key: `user-${user.id.toString()}`,
    path: `/admin/account/${user.id.toString()}`,
    user,
  }));

  // Either request can be the one that put the panel in its error state.
  const retry = useCallback(() => {
    retryEvents();
    retryUsers();
  }, [retryEvents, retryUsers]);

  const account = useAccountActions();
  // useFuzzySearch answers an empty query with every action, which is what the idle panel shows.
  const matchedActions = useFuzzySearch(
    account?.actions,
    isActive ? debouncedQuery : '',
    ActionSearchFields,
    0,
  );
  const actionItems: Array<ActionSearchItem> = matchedActions.map((action) => ({
    kind: 'action',
    key: `action-${action.key}`,
    action,
  }));

  const recentItems: Array<SearchItem> = isActive
    ? []
    : recentSearches.map((recent) => ({
        kind: 'recent' as const,
        key: `recent-${recent}`,
        query: recent,
      }));

  // One table behind both the tab counts and the rendered groups, so the two cannot disagree.
  const categories: Array<{
    key: SearchCategory;
    title: string;
    items: Array<SearchItem>;
    hasMore: boolean;
    shownUnderAll?: number;
  }> = [
    {
      key: SearchCategory.Events,
      title: 'Events',
      items: eventItems,
      hasMore: hasNextPage,
      shownUnderAll: EventsShownUnderAll,
    },
    { key: SearchCategory.Pages, title: 'Pages', items: pageItems, hasMore: false },
    {
      key: SearchCategory.Users,
      title: 'Users',
      items: userItems,
      // The endpoint gives no total, so a full page means there may be more.
      hasMore: userItems.length >= UserSearchLimit,
    },
  ];
  const matched = categories.filter((entry) => entry.items.length > 0);
  // One category is not a choice, and the panel's group heading already names it.
  const tabs: Array<SearchTab> =
    matched.length > 1
      ? [
          {
            key: SearchCategory.All,
            title: 'All',
            count: matched.reduce((sum, entry) => sum + entry.items.length, 0),
            hasMore: hasNextPage,
          },
          ...matched.map(({ key, title, items, hasMore }) => ({
            key,
            title,
            count: items.length,
            hasMore,
          })),
        ]
      : [];
  const activeCategory = tabs.some((tab) => tab.key === category) ? category : SearchCategory.All;
  const showAll = activeCategory === SearchCategory.All;
  // All only has to compress a group when it is blending several.
  const blended = showAll && matched.length > 1;

  const groups: Array<SearchGroup> = [];
  // First, because it is about the page you are on. Not a tab: it answers the page, not the query.
  if (showAll && account != null && actionItems.length > 0)
    groups.push({
      key: 'account-actions',
      title: `This account · ${account.user.name || account.user.email}`,
      items: actionItems,
    });
  groups.push(
    ...matched
      .filter((entry) => showAll || entry.key === activeCategory)
      .map(({ key, title, items, shownUnderAll }) => ({
        key,
        title,
        items: blended && shownUnderAll != null ? items.slice(0, shownUnderAll) : items,
      })),
  );
  if (recentItems.length > 0) groups.push({ key: 'recent', title: 'Recent', items: recentItems });
  if (quickActions.length > 0)
    groups.push({ key: 'quick-actions', title: 'Jump to', items: quickActions });

  const eventsVisible = showAll || activeCategory === SearchCategory.Events;
  const usersVisible = isAdmin && (showAll || activeCategory === SearchCategory.Users);

  return {
    groups,
    items: groups.flatMap((group) => group.items),
    tabs,
    activeCategory,
    debouncedQuery,
    isActive,
    // Narrowed to the visible groups, or one category's request drives another category's panel.
    isLoading: isActive && ((eventsVisible && isLoading) || (usersVisible && isLoadingUsers)),
    // A failed users group just disappears, unless nothing else answered — then the panel would
    // claim "No results found."
    hasError:
      isActive &&
      ((eventsVisible && hasError) || (usersVisible && usersError != null && groups.length === 0)),
    retry,
    // Scrolling for more only makes sense where the whole list is shown.
    hasNextPage: isActive && !hasError && hasNextPage && eventsVisible && !blended,
    loadNextPage,
  };
};
